import { useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { SkeletonRows } from '../../components/skeleton'
import { getDashboard } from '../../services/platform'
import { onQuerySuccess } from '../../utils/queryResponse'
import { dateLocale } from '../../i18n/i18n'

const AVATAR_COLORS = ['#3B82F6', '#F59E0B', '#10B981', '#8B5CF6', '#EF4444', '#EC4899', '#06B6D4', '#F97316']
const getInitials = (name) => { if (!name) return '?'; const parts = name.trim().split(/\s+/); if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase(); return name.substring(0, 2).toUpperCase() }
const getAvatarColor = (name) => { let hash = 0; for (let i = 0; i < (name || '').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash); return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length] }

const formatDateTime = (dateStr) => {
    if (!dateStr) return '—'
    return new Date(dateStr).toLocaleString(dateLocale(), { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

const formatDate = (dateStr) => {
    if (!dateStr) return '—'
    return new Date(dateStr).toLocaleDateString(dateLocale(), { day: '2-digit', month: 'short', year: 'numeric' })
}

const formatRevenue = (val) => {
    const num = Number(val) || 0
    if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(1)}M`
    if (num >= 1_000) return `$${(num / 1_000).toFixed(1)}K`
    return `$${num.toFixed(0)}`
}

// With `to` the whole card is a link (arrow shown); without it the card is
// plain, so no arrow promises a page that doesn't exist.
const StatCard = ({ to, ...props }) => to
    ? <Link to={to} className="pd-stat-card pd-stat-link"><StatCardBody {...props} linked /></Link>
    : <div className="pd-stat-card"><StatCardBody {...props} /></div>

const StatCardBody = ({ number, label, desc, iconBg, icon, linked }) => (
    <>
        <div className="pd-stat-top">
            <div className="pd-stat-icon" style={{ background: iconBg }}>{icon}</div>
            {linked && <svg className="pd-stat-arrow" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>}
        </div>
        <div className="pd-stat-label">{label}</div>
        <div className="pd-stat-number-row">
            <span className="pd-stat-number">{number ?? 0}</span>
            <span style={{ fontSize: 12, color: '#16A34A', display: 'inline-flex', alignItems: 'center', gap: 3, fontWeight: 600 }}>
                <svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="#16A34A" strokeWidth="2"><polyline points="2 10 6 6 10 8 14 4" /></svg>
                0%
            </span>
        </div>
        {desc && <div className="pd-stat-desc">{desc}</div>}
    </>
)

const PlatformDashboard = () => {
    const { t } = useTranslation()
    const [stats, setStats] = useState(null)
    const [recentCompanies, setRecentCompanies] = useState([])
    const [recentUsers, setRecentUsers] = useState([])

    const { data, isFetching } = useQuery('platform-dashboard', getDashboard)

    useEffect(() => {
        onQuerySuccess(data, (result) => {
            setStats(result.stats)
            setRecentCompanies(result.recentCompanies || [])
            setRecentUsers(result.recentUsers || [])
        })
    }, [data])

    return (
        <div className="sbr">
            <div style={{ marginBottom: 24 }}>
                <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: '#0B1424' }}>{t('platform.dashboard.title')}</h2>
                <p style={{ fontSize: 13, color: '#697586', margin: 0 }}>{t('platform.dashboard.subtitle')}</p>
            </div>

            {!stats && isFetching ? (
                <div className="card-sbr" style={{ padding: '24px 20px' }}>
                    <SkeletonRows rows={4} widths={['25%', '25%', '25%', '25%']} />
                </div>
            ) : (
                <>
                    {/* Row 1 */}
                    <div className="pd-stats-row">
                        <StatCard
                            to="/platform/companies"
                            number={stats?.totalCompanies}
                            label={t('platform.dashboard.totalCompanies')}
                            desc={t('platform.dashboard.totalCompaniesDesc')}
                            iconBg="rgba(59,130,246,.12)"
                            icon={<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#3B82F6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2" /><path d="M9 22v-4h6v4" /><path d="M8 6h.01" /><path d="M16 6h.01" /><path d="M8 10h.01" /><path d="M16 10h.01" /></svg>}
                        />
                        <StatCard
                            to="/platform/companies?status=active"
                            number={stats?.activeCompanies}
                            label={t('platform.dashboard.activeCompanies')}
                            desc={t('platform.dashboard.activeCompaniesDesc')}
                            iconBg="rgba(16,185,129,.12)"
                            icon={<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#10B981" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>}
                        />
                        <StatCard
                            to="/platform/companies?status=suspended"
                            number={stats?.suspendedCompanies}
                            label={t('platform.dashboard.suspended')}
                            desc={t('platform.dashboard.suspendedDesc')}
                            iconBg="rgba(239,68,68,.12)"
                            icon={<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#EF4444" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" /></svg>}
                        />
                        <StatCard
                            to="/platform/plans"
                            number={stats?.totalPlans}
                            label={t('platform.dashboard.plans')}
                            desc={t('platform.dashboard.plansDesc')}
                            iconBg="rgba(59,130,246,.12)"
                            icon={<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#3B82F6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" /></svg>}
                        />
                    </div>

                    {/* Row 2 */}
                    <div className="pd-stats-row">
                        <StatCard
                            to="/platform/users"
                            number={stats?.totalUsers}
                            label={t('platform.dashboard.totalUsers')}
                            desc={t('platform.dashboard.totalUsersDesc')}
                            iconBg="rgba(59,130,246,.12)"
                            icon={<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#3B82F6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>}
                        />
                        <StatCard
                            to="/platform/users?status=active"
                            number={stats?.activeUsers}
                            label={t('platform.dashboard.activeUsers')}
                            desc={t('platform.dashboard.activeUsersDesc')}
                            iconBg="rgba(16,185,129,.12)"
                            icon={<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#10B981" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /><polyline points="16 3 18 5 22 1" /></svg>}
                        />
                        <StatCard
                            number={stats?.totalJobs}
                            label={t('platform.dashboard.totalJobs')}
                            desc={t('platform.dashboard.totalJobsDesc')}
                            iconBg="rgba(59,130,246,.12)"
                            icon={<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#3B82F6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" /></svg>}
                        />
                        <StatCard
                            number={stats?.activeJobs}
                            label={t('platform.dashboard.activeJobs')}
                            desc={t('platform.dashboard.activeJobsDesc')}
                            iconBg="rgba(16,185,129,.12)"
                            icon={<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#10B981" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>}
                        />
                    </div>

                    {/* Revenue banner */}
                    <div className="pd-revenue" style={{
                        background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
                        borderRadius: 16, padding: '24px 28px', marginBottom: 28,
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', overflow: 'hidden',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16, zIndex: 1 }}>
                            <div style={{
                                width: 48, height: 48, borderRadius: 14,
                                background: 'rgba(16,185,129,.18)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>
                            </div>
                            <div>
                                <div style={{ fontSize: 13, color: 'rgba(255,255,255,.6)', fontWeight: 500 }}>{t('platform.dashboard.totalRevenue')}</div>
                                <div style={{ fontSize: 32, fontWeight: 800, color: '#fff', lineHeight: 1.1 }}>{formatRevenue(stats?.totalRevenue)}</div>
                            </div>
                        </div>
                        <div style={{ position: 'absolute', left: '30%', right: '20%', top: 0, bottom: 0, opacity: .15, zIndex: 0 }}>
                            <svg viewBox="0 0 400 100" width="100%" height="100%" preserveAspectRatio="none">
                                <polyline fill="none" stroke="#10B981" strokeWidth="2" points="0,70 40,60 80,65 120,40 160,50 200,30 240,45 280,20 320,35 360,15 400,25" />
                            </svg>
                        </div>
                        <div style={{
                            background: 'rgba(16,185,129,.15)', borderRadius: 20, padding: '6px 14px',
                            display: 'flex', alignItems: 'center', gap: 6, zIndex: 1,
                        }}>
                            <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="#10B981" strokeWidth="2"><polyline points="2 10 6 6 10 8 14 4" /></svg>
                            <span style={{ fontSize: 13, fontWeight: 700, color: '#10B981' }}>+12%</span>
                            <span style={{ fontSize: 11, color: 'rgba(255,255,255,.5)' }}>{t('platform.dashboard.vsLast30')}</span>
                        </div>
                    </div>

                    {/* Recent tables */}
                    <div className="pd-recent-grid">
                        {/* Recent Companies */}
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#697586" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2" /><path d="M9 22v-4h6v4" /></svg>
                                    <div>
                                        <div style={{ fontSize: 14, fontWeight: 700, color: '#0B1424' }}>{t('platform.dashboard.recentCompanies')}</div>
                                        <div style={{ fontSize: 11, color: '#9CA3B0' }}>{t('platform.dashboard.recentCompaniesDesc')}</div>
                                    </div>
                                </div>
                                <Link to="/platform/companies" style={{ fontSize: 12, fontWeight: 600, color: '#3B82F6', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
                                    {t('platform.common.viewAll')} <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
                                </Link>
                            </div>
                            <div className="card-sbr" style={{ padding: 0, flex: 1 }}>
                                <table className="p-table">
                                    <thead>
                                        <tr><th>{t('platform.columns.company')}</th><th className="p-col-opt">{t('platform.columns.plan')}</th><th className="p-col-opt">{t('platform.columns.users')}</th><th>{t('platform.columns.created')}</th></tr>
                                    </thead>
                                    <tbody>
                                        {recentCompanies.map(c => (
                                            <tr key={c.id}>
                                                <td>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <div style={{ width: 30, height: 30, borderRadius: '50%', background: getAvatarColor(c.name), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff', flexShrink: 0 }}>{getInitials(c.name)}</div>
                                                        <span style={{ fontSize: 13, fontWeight: 600, color: '#0B1424' }}>{c.name}</span>
                                                    </div>
                                                </td>
                                                <td className="p-col-opt" style={{ fontSize: 12, color: '#697586' }}>{c.plan || '—'}</td>
                                                <td className="p-col-opt" style={{ fontSize: 12, color: '#697586' }}>{c.userCount}</td>
                                                <td style={{ fontSize: 12, color: '#697586' }}>{formatDate(c.createdAt)}</td>
                                            </tr>
                                        ))}
                                        {recentCompanies.length === 0 && (
                                            <tr><td colSpan={4} style={{ textAlign: 'center', color: '#9CA3B0', padding: 20 }}>{t('platform.dashboard.noCompaniesYet')}</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Recent Users */}
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#697586" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                                    <div>
                                        <div style={{ fontSize: 14, fontWeight: 700, color: '#0B1424' }}>{t('platform.dashboard.recentUsers')}</div>
                                        <div style={{ fontSize: 11, color: '#9CA3B0' }}>{t('platform.dashboard.recentUsersDesc')}</div>
                                    </div>
                                </div>
                                <Link to="/platform/users" style={{ fontSize: 12, fontWeight: 600, color: '#3B82F6', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
                                    {t('platform.common.viewAll')} <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
                                </Link>
                            </div>
                            <div className="card-sbr" style={{ padding: 0, flex: 1 }}>
                                <table className="p-table">
                                    <thead>
                                        <tr><th>{t('platform.columns.user')}</th><th className="p-col-opt">{t('platform.columns.lastLogin')}</th><th>{t('platform.columns.joined')}</th></tr>
                                    </thead>
                                    <tbody>
                                        {recentUsers.map(u => (
                                            <tr key={u.id}>
                                                <td>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <div style={{ width: 30, height: 30, borderRadius: '50%', background: getAvatarColor(u.fullName), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff', flexShrink: 0 }}>{getInitials(u.fullName)}</div>
                                                        <div>
                                                            <div style={{ fontSize: 13, fontWeight: 600, color: '#0B1424' }}>{u.fullName}</div>
                                                            <div style={{ fontSize: 11, color: '#9CA3B0' }}>{u.email}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="p-col-opt" style={{ fontSize: 12, color: '#697586' }}>{formatDateTime(u.lastLoginAt)}</td>
                                                <td style={{ fontSize: 12, color: '#697586' }}>{formatDate(u.createdAt)}</td>
                                            </tr>
                                        ))}
                                        {recentUsers.length === 0 && (
                                            <tr><td colSpan={3} style={{ textAlign: 'center', color: '#9CA3B0', padding: 20 }}>{t('platform.dashboard.noUsersYet')}</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}

export default PlatformDashboard
